import { NgModule, RendererFactory2 } from '@angular/core';
import { CommonModule as AngularCore, DOCUMENT } from '@angular/common';

import { ScrollStrategyOptions, OverlayModule } from '@angular/cdk/overlay';
import { PortalModule } from '@angular/cdk/portal';
import { WINDOW_TOKEN } from '../services/bootstrap.service';
import { FormModule } from './form/form.module';
import { RadioModule } from './radio/radio.module';
import { TabComponent } from './tabs/tab.component';
import { TabsComponent } from './tabs/tabs.component';
import { TabsToolbarComponent } from './tabs/tabs-toolbar.component';
import { TabStateNameDirective } from './tabs/tabs-state-name.directive';
import { TabsNavigationDirective } from './tabs/tabs-navigation.directive';
import { TabsScrollComponent } from './tabs/tabs-scroll.component';
import { ChartsModule } from './charts/charts.module';
import { PaginationComponent } from './pagination/pagination.component';
import { PaginationSliderComponent } from './pagination/pagination-slider/pagination-slider.component';
import { ModalCloseDirective } from './modal/components/modal-close/modal-close-button.directive';
import { ModalHeaderButtonsComponent } from './modal/components/modal-header/modal-header-buttons.component';
import { ModalHeaderToolbarComponent } from './modal/components/modal-header/modal-header-toolbar.component';
import { ModalHeaderCloseButtonComponent } from './modal/components/modal-header/modal-header-close-button.component';
import { ModalHeaderBackButtonComponent } from './modal/components/modal-header/modal-header-back-button.component';
import { ModalHeaderTitleComponent } from './modal/components/modal-header/modal-header-title.component';
import { ModalHeaderComponent } from './modal/components/modal-header/modal-header.component';
import { ModalSubHeaderComponent } from './modal/components/modal-sub-header/modal-sub-header.component';
import { ModalWindowComponent } from './modal/components/modal-window/modal-window.component';
import { MODAL_SCROLL_STRATEGY_FACTORY, MODAL_SCROLL_STRATEGY_PROVIDER_FACTORY } from './modal/modal-scroll-strategy-factory';
import { MODAL_CONTAINER } from './modal/modal.domain';
import { SystemErrorComponent } from './system-error/system-error.component';
import { InputModule } from './input/input.module';
import { OverlayToggleDirective } from './overlay/overlay-toggle.directive';
import { OverlayComponent } from './overlay/overlay.component';
import { OverlayHeaderComponent } from './overlay/overlay-header.component';
import { OverlayOriginDirective } from './overlay/overlay-origin.directive';
import { OVERLAY_FACTORY, OverlayFactoryService } from './overlay/overlay-factory.service';
import { StepsComponent } from './steps/steps.component';
import { StepComponent } from './steps/step.component';
import { StepTitleComponent } from './steps/step-title.component';
import { HelpLinkModule } from './help-link/help-link.module';
import { LoadingIndicatorModule } from './loading-indicator/loading-indicator.module';
import { OpenWindowDirective } from './open-window/open-window.directive';
import { NumberSpinnerComponent } from './number-spinner/number-spinner.component';
import { DateRangeSelectorModule } from './date-range-selector/date-range-selector.module';
import { IncludeComponent } from './include/include.component';
import { DropdownComponent } from './dropdown/dropdown.component';
import { DropdownItemComponent } from './dropdown/dropdown-item.component';
import { DropdownToggleDirective } from './dropdown/dropdown-toggle.directive';
import { DropdownPlaceholderDirective } from './dropdown/dropdown-placeholder.directive';
import { DropdownItemClickDirective } from './dropdown/dropdown-item-click.directive';
import { ScrollableDirective } from './scroll/scrollable.directive';
import { ScrollableContainerComponent } from './scroll/scrollable-container.component';
import { TableCardModule } from './table-card/table-card.module';
import { SliderComponent } from './slider/slider.component';
import { SliderItemComponent } from './slider/slider-item.component';
import { SideSheetSelectorComponent } from './side-sheet-selector/side-sheet-selector.component';
import { SideSheetSelectorItemComponent } from './side-sheet-selector/side-sheet-selector-item.component';
import { SideSheetSelectorToggleDirective } from './side-sheet-selector/side-sheet-selector-toggle.directive';
import { SideSheetSelectorToggleStaticDirective } from './side-sheet-selector/side-sheet-selector-toggle-static.directive';
import { SIDE_SHEET_SELECTOR_SCROLL_STRATEGY_PROVIDER } from './side-sheet-selector/side-sheet-selector.domain';
import { NgxDatatableModule } from './datatable/ngx-datatable.module';
import { StepTrackerModule } from './step-tracker/step-tracker.module';

import { AboutDialogComponent } from './about-dialog/about-dialog.component';
import { BackToTopComponent } from './back-to-top/back-to-top.component';
import { BadgeComponent } from './badge/badge.component';
import { BlockDirective } from './block/block.directive';
import { BottomModalComponent } from './bottom-modal/bottom-modal.component';
import { ButtonComponent } from './button/button.component';
import { CardGroupComponent } from './card/card-group.component';
import { CardComponent } from './card/card.component';
import { CardPrimaryActionComponent } from './card/card-primary-action.component';
import { CardMediaDirective } from './card/card-media.directive';
import { CardHeaderComponent } from './card/card-header.component';
import { CardHeaderTitleAreaComponent } from './card/card-header-title-area.component';
import { CardFooterComponent } from './card/card-footer.component';
import { CardContentComponent } from './card/card-content.component';
import { CardSubtitleComponent } from './card/card-subtitle.component';
import { CardTitleComponent } from './card/card-title.component';
import { CheckboxComponent } from './checkbox/checkbox.component';
import { CheckboxGroupComponent } from './checkbox/checkbox-group.component';
import { ChipComponent } from './chips/chip.component';
import { ChipListComponent } from './chips/chip-list.component';
import { ClearFieldComponent } from './clear-field/clear-field.component';
import { ConfirmComponent } from './confirm/confirm.component';
import { ConfirmButtonComponent } from './confirm/confirm-button.component';
import { ConfirmButtonsComponent } from './confirm/confirm-buttons.component';
import { ConfirmButtonOkComponent } from './confirm/confirm-button-ok.component';
import { ConfirmButtonCancelComponent } from './confirm/confirm-button-cancel.component';
import { ConfirmTitleComponent } from './confirm/confirm-title.component';
import { ConfirmBodyComponent } from './confirm/confirm-body.component';
import { CurrencySelectorComponent } from './currency-selector/currency-selector.component';
import { ExpandIconComponent } from './expand-icon/expand-icon.component';
import { ExportComponent } from './export/export.component';
import { FileUploadComponent } from './file-upload/file-upload.component';
import { FileUploadCounterComponent } from './file-upload/file-upload-counter.component';
import { FileUploadSizeValidator } from './file-upload/file-upload-size-validator.directive';
import { FileUploadMaxValidator } from './file-upload/file-upload-max-validator.directive';
import { FileUploadMinValidator } from './file-upload/file-upload-min-validator.directive';
import { FileUploadTotalSizeValidator } from './file-upload/file-upload-total-size-validator.directive';
import { FileUploadDuplicateValidator } from './file-upload/file-upload-duplicate-validator.directive';
import { FileUploadExtensionValidator } from './file-upload/file-upload-extension-validator.directive';
import { FlyoutMenuToggleDirective } from './flyout-menu/flyout-menu-toggle.directive';
import { FlyoutMenuComponent } from './flyout-menu/flyout-menu.component';
import { FlyoutMenuItemDirective } from './flyout-menu/flyout-menu-item.directive';
import { FlyoutMenuHeaderComponent } from './flyout-menu/flyout-menu-header.component';
import { FlyoutMenuFooterComponent } from './flyout-menu/flyout-menu-footer.component';
import { FocusDirective } from './focus/focus.directive';
import { FocusAnchorComponent } from './focus/focus-anchor.component';
import { FocusOnDisabledDirective } from './focus/focus-on-disabled.directive';
import { IconComponent } from './icon/icon.component';
import { IconButtonComponent } from './icon-button/icon-button.component';
import { IconFlagComponent } from './icon/icon-flag.component';
import { IconArrowComponent } from './icon/icon-arrow.component';
import { MinHeightPanelComponent } from './panel/min-height/min-height-panel.component';
import { MinHeightPanelTriggerDirective } from './panel/min-height/min-height-panel-trigger.directive';
import { SplitButtonComponent } from './split-buttons/split-button.component';
import { SplitButtonsComponent } from './split-buttons/split-buttons.component';
import { SplitButtonDataComponent } from './split-buttons/split-button-data.component';
import { SplitButtonTitleComponent } from './split-buttons/split-button-title.component';
import { NewTypeAheadComponent } from './typeahead/new-typeahead.component';
import { PanelComponent } from './panel/panel.component';
import { PanelStartComponent } from './panel/panel-start.component';
import { PanelEndComponent } from './panel/panel-end.component';
import { PanelSubscriptComponent } from './panel/panel-subscript.component';
import { SelectableInputPanelComponent } from './selectable-input-panel/selectable-input-panel.component';
import { NotificationComponent } from './notification/notification.component';
import { NotificationHeaderComponent } from './notification/notification-header.component';
import { NotificationCloseButtonComponent } from './notification/notification-close-button.component';
import { NotificationIconDirective } from './notification/notification-icon.directive';
import { DividerComponent } from './panel/divider.component';
import { ListItemComponent } from './list/list-item.component';
import { ListItemLabelComponent } from './list/list-item-label.component';
import { ListComponent } from './list/list.component';
import { ListItemValueComponent } from './list/list-item-value.component';
import { LinearListComponent } from './linear-list/linear-list.component';
import { LinearListItemComponent } from './linear-list/linear-list-item.component';
import { LinearListItemLabelComponent } from './linear-list/linear-list-item-label.component';
import { LinearListItemTemplateDirective } from './linear-list/linear-list-item-template.directive';
import { AvatarComponent } from './avatar/avatar.component';
import { SelectButtonDirective } from './button/select-button.component';
import { RangeSliderComponent } from './range-slider/range-slider.component';
import { RangeInputDirective } from './range-slider/range-slider.directive';
import { ToggleComponent } from './toggle/toggle.component';
import { ToggleLabelComponent } from './toggle/toggle-label.component';
import { ToggleLabelItemComponent } from './toggle/toggle-label-item.component';
import { MaterialPhoneInputComponent } from './material-phone-input/material-phone-input.component';
import { MaterialFormChipsComponent } from './material-form-chips/material-form-chips.component';
import { DetailedLoadingIndicatorComponent } from './detailed-loading-indicator/detailed-loading-indicator.component';
import { InputCurrencySelectorComponent } from './input-currency-selector/input-currency-selector.component';

@NgModule({
  imports: [
    AngularCore,
    OverlayModule,
    PortalModule,

    BlockDirective,
    CardMediaDirective,
    FlyoutMenuItemDirective,
    FlyoutMenuToggleDirective,
    FocusDirective,
    FocusOnDisabledDirective,
    LinearListItemTemplateDirective,
    MinHeightPanelTriggerDirective,
    RangeInputDirective,
    SelectButtonDirective,
    AboutDialogComponent,
    BackToTopComponent,
    BadgeComponent,
    BottomModalComponent,
    ButtonComponent,
    CardGroupComponent,
    CardComponent,
    CardPrimaryActionComponent,
    CardContentComponent,
    CardFooterComponent,
    CardHeaderComponent,
    CardHeaderTitleAreaComponent,
    CardSubtitleComponent,
    CardTitleComponent,
    CheckboxComponent,
    CheckboxGroupComponent,
    ChipComponent,
    ChipListComponent,
    ClearFieldComponent,
    ConfirmBodyComponent,
    ConfirmButtonCancelComponent,
    ConfirmButtonComponent,
    ConfirmButtonOkComponent,
    ConfirmButtonsComponent,
    ConfirmComponent,
    ConfirmTitleComponent,
    CurrencySelectorComponent,
    DetailedLoadingIndicatorComponent,
    DividerComponent,
    ExpandIconComponent,
    ExportComponent,
    FileUploadComponent,
    FileUploadCounterComponent,
    FileUploadSizeValidator,
    FileUploadMaxValidator,
    FileUploadMinValidator,
    FileUploadTotalSizeValidator,
    FileUploadDuplicateValidator,
    FileUploadExtensionValidator,
    FlyoutMenuComponent,
    FlyoutMenuFooterComponent,
    FlyoutMenuHeaderComponent,
    FocusAnchorComponent,
    IconArrowComponent,
    IconComponent,
    IconButtonComponent,
    IconFlagComponent,
    IncludeComponent,
    LinearListComponent,
    LinearListItemComponent,
    LinearListItemLabelComponent,
    ListComponent,
    ListItemComponent,
    ListItemLabelComponent,
    ListItemValueComponent,
    MaterialPhoneInputComponent,
    MaterialFormChipsComponent,
    MinHeightPanelComponent,
    NotificationCloseButtonComponent,
    NotificationIconDirective,
    NotificationComponent,
    NotificationHeaderComponent,
    PanelComponent,
    PanelStartComponent,
    PanelEndComponent,
    PanelSubscriptComponent,
    SelectableInputPanelComponent,
    SplitButtonComponent,
    SplitButtonDataComponent,
    SplitButtonsComponent,
    SplitButtonTitleComponent,
    NewTypeAheadComponent,
    AvatarComponent,
    RangeSliderComponent,
    ToggleComponent,
    ToggleLabelComponent,
    ToggleLabelItemComponent,
    ChartsModule,
    DateRangeSelectorModule,
    DropdownComponent,
    DropdownItemComponent,
    DropdownToggleDirective,
    DropdownPlaceholderDirective,
    DropdownItemClickDirective,
    FormModule,
    HelpLinkModule,
    InputModule,
    LoadingIndicatorModule,
    ModalWindowComponent,
    ModalHeaderComponent,
    ModalHeaderTitleComponent,
    ModalSubHeaderComponent,
    ModalHeaderCloseButtonComponent,
    ModalHeaderBackButtonComponent,
    ModalHeaderButtonsComponent,
    ModalHeaderToolbarComponent,
    ModalCloseDirective,
    NgxDatatableModule,
    NumberSpinnerComponent,
    OpenWindowDirective,
    OverlayToggleDirective,
    OverlayComponent,
    OverlayHeaderComponent,
    OverlayOriginDirective,
    PaginationComponent,
    PaginationSliderComponent,
    RadioModule,
    ScrollableDirective,
    ScrollableContainerComponent,
    SideSheetSelectorComponent,
    SideSheetSelectorItemComponent,
    SideSheetSelectorToggleDirective,
    SideSheetSelectorToggleStaticDirective,
    SliderComponent,
    SliderItemComponent,
    StepsComponent,
    StepComponent,
    StepTitleComponent,
    SystemErrorComponent,
    TableCardModule,
    TabComponent,
    TabsComponent,
    TabsToolbarComponent,
    TabStateNameDirective,
    TabsNavigationDirective,
    TabsScrollComponent,
    StepTrackerModule,
    InputCurrencySelectorComponent
  ],
  exports: [
    BlockDirective,
    CardMediaDirective,
    FlyoutMenuItemDirective,
    FlyoutMenuToggleDirective,
    FocusDirective,
    FocusOnDisabledDirective,
    LinearListItemTemplateDirective,
    MinHeightPanelTriggerDirective,
    RangeInputDirective,
    SelectButtonDirective,
    AboutDialogComponent,
    BackToTopComponent,
    BadgeComponent,
    BottomModalComponent,
    ButtonComponent,
    CardGroupComponent,
    CardComponent,
    CardPrimaryActionComponent,
    CardContentComponent,
    CardFooterComponent,
    CardHeaderComponent,
    CardHeaderTitleAreaComponent,
    CardSubtitleComponent,
    CardTitleComponent,
    CheckboxComponent,
    CheckboxGroupComponent,
    ChipComponent,
    ChipListComponent,
    ClearFieldComponent,
    ConfirmBodyComponent,
    ConfirmButtonCancelComponent,
    ConfirmButtonComponent,
    ConfirmButtonOkComponent,
    ConfirmButtonsComponent,
    ConfirmComponent,
    ConfirmTitleComponent,
    CurrencySelectorComponent,
    DetailedLoadingIndicatorComponent,
    DividerComponent,
    ExpandIconComponent,
    ExportComponent,
    FileUploadComponent,
    FileUploadCounterComponent,
    FileUploadSizeValidator,
    FileUploadMaxValidator,
    FileUploadMinValidator,
    FileUploadTotalSizeValidator,
    FileUploadDuplicateValidator,
    FileUploadExtensionValidator,
    FlyoutMenuComponent,
    FlyoutMenuFooterComponent,
    FlyoutMenuHeaderComponent,
    FocusAnchorComponent,
    IconArrowComponent,
    IconComponent,
    IconButtonComponent,
    IconFlagComponent,
    IncludeComponent,
    LinearListComponent,
    LinearListItemComponent,
    LinearListItemLabelComponent,
    ListComponent,
    ListItemComponent,
    ListItemLabelComponent,
    ListItemValueComponent,
    MaterialPhoneInputComponent,
    MaterialFormChipsComponent,
    MinHeightPanelComponent,
    NotificationCloseButtonComponent,
    NotificationIconDirective,
    NotificationComponent,
    NotificationHeaderComponent,
    PanelComponent,
    PanelStartComponent,
    PanelEndComponent,
    PanelSubscriptComponent,
    RangeSliderComponent,
    SelectableInputPanelComponent,
    SplitButtonComponent,
    SplitButtonDataComponent,
    SplitButtonsComponent,
    SplitButtonTitleComponent,
    ToggleComponent,
    ToggleLabelComponent,
    ToggleLabelItemComponent,
    NewTypeAheadComponent,
    AvatarComponent,
    ChartsModule,
    DateRangeSelectorModule,
    DropdownComponent,
    DropdownItemComponent,
    DropdownToggleDirective,
    DropdownPlaceholderDirective,
    DropdownItemClickDirective,
    FormModule,
    HelpLinkModule,
    InputModule,
    LoadingIndicatorModule,
    ModalWindowComponent,
    ModalHeaderComponent,
    ModalHeaderTitleComponent,
    ModalSubHeaderComponent,
    ModalHeaderCloseButtonComponent,
    ModalHeaderBackButtonComponent,
    ModalHeaderButtonsComponent,
    ModalHeaderToolbarComponent,
    ModalCloseDirective,
    NgxDatatableModule,
    NumberSpinnerComponent,
    OpenWindowDirective,
    OverlayToggleDirective,
    OverlayComponent,
    OverlayHeaderComponent,
    OverlayOriginDirective,
    PaginationComponent,
    PaginationSliderComponent,
    RadioModule,
    ScrollableDirective,
    ScrollableContainerComponent,
    SideSheetSelectorComponent,
    SideSheetSelectorItemComponent,
    SideSheetSelectorToggleDirective,
    SideSheetSelectorToggleStaticDirective,
    SliderComponent,
    SliderItemComponent,
    StepsComponent,
    StepComponent,
    StepTitleComponent,
    SystemErrorComponent,
    TableCardModule,
    TabComponent,
    TabsComponent,
    TabsToolbarComponent,
    TabStateNameDirective,
    TabsNavigationDirective,
    TabsScrollComponent,
    StepTrackerModule,
    InputCurrencySelectorComponent
  ],
  providers: [
    {
      provide: OVERLAY_FACTORY,
      useExisting: OverlayFactoryService
    },
    SIDE_SHEET_SELECTOR_SCROLL_STRATEGY_PROVIDER,
    {
      provide: WINDOW_TOKEN,
      useValue: window
    },
    { provide: MODAL_SCROLL_STRATEGY_FACTORY,
      useFactory: MODAL_SCROLL_STRATEGY_PROVIDER_FACTORY,
      deps: [DOCUMENT, WINDOW_TOKEN, RendererFactory2, ScrollStrategyOptions]
    },
    {
      provide: MODAL_CONTAINER,
      useValue: ModalWindowComponent
    }
  ]
})
export class ComponentsModule {
}
